import {useSortable} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {Box, ListItem} from "@mui/material";
import Typography from "@mui/material/Typography";
import React from "react";
import {useTranslation} from "react-i18next";
import {MissingLanguagesMarker, RowDragHandle, RowMenuButton} from "./SurveyQuestionRow";

export type SectionDividerData = {
    /** The break's id (`break-${id of the section's first question}`), the sortable id of a divider that moves. */
    id: string
    /** Counted down the list: "Section 1" is the fixed header above the first question. */
    number: number
    /** The section's title as plain text in the editing language; empty without one. */
    title: string
    missingLanguages: string[]
    /** The section's first question, which a click on the divider selects. */
    questionId: string
};

interface BaseProps {
    divider: SectionDividerData
    lifted?: boolean
    /** None on "Section 1", which never moves. */
    handleProps?: Record<string, unknown>
    onSelect: () => void
    /** None on "Section 1": there is no section above it to merge with. */
    onOpenMenu?: (anchor: HTMLElement) => void
}

export function SurveySectionDividerBase({
    divider,
    lifted,
    handleProps,
    onSelect,
    onOpenMenu
}: BaseProps) {
    const {t} = useTranslation();

    const sectionLabel = t('Section {{n}}', {n: divider.number});
    // The row shows the title alone; its name says which section it is, too.
    const name = divider.title ? `${sectionLabel}: ${divider.title}` : sectionLabel;

    return (
        <ListItem
            disableGutters
            aria-label={name}
            onClick={onSelect}
            sx={{
                minHeight: 36,
                px: 1,
                gap: 1,
                borderRadius: 1,
                cursor: 'pointer',
                ...(lifted && {
                    bgcolor: 'background.paper',
                    boxShadow: 3,
                }),
                '&:hover': {bgcolor: 'action.hover'},
            }}
        >
            <RowDragHandle handleProps={handleProps} lifted={lifted}/>

            <Typography
                noWrap
                sx={{minWidth: 0, fontSize: 13, fontWeight: 600, color: 'text.secondary'}}
                title={divider.title}
            >
                {divider.title || sectionLabel}
            </Typography>

            <Box sx={{flex: 1, minWidth: 16, borderTop: 1, borderColor: 'divider'}}/>

            <MissingLanguagesMarker languages={divider.missingLanguages}/>

            {!!onOpenMenu && <RowMenuButton name={name} onOpenMenu={onOpenMenu}/>}
        </ListItem>
    );
}

interface Props {
    divider: SectionDividerData
    onSelect: () => void
    onOpenMenu: (anchor: HTMLElement) => void
}

function SurveySectionDivider({divider, onSelect, onOpenMenu}: Props) {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
        id: divider.id,
    });

    return (
        // The space above a divider is padding, not margin: the sorting measures it with the row, so the rows
        // around a drag keep their even gaps.
        <Box
            ref={setNodeRef}
            sx={{
                pt: 1,
                mb: 0.5,
                transform: CSS.Transform.toString(transform),
                transition,
            }}
        >
            <Box sx={{borderRadius: 1, ...(isDragging && {bgcolor: 'action.hover'})}}>
                <Box sx={{...(isDragging && {visibility: 'hidden'})}}>
                    <SurveySectionDividerBase
                        divider={divider}
                        handleProps={{...attributes, ...listeners}}
                        onSelect={onSelect}
                        onOpenMenu={onOpenMenu}
                    />
                </Box>
            </Box>
        </Box>
    );
}

export default SurveySectionDivider;
